import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authMeAPI } from '../services/api';
import { getToken } from '../services/authToken';

interface RoleInfo {
  id: number;
  role_code: string;
  role_name: string;
  is_system: boolean;
}

interface MenuPermission {
  menu_code: string;
  menu_name: string;
  menu_group: string;
  menu_path: string;
  actions: string[];
}

interface PermissionState {
  loaded: boolean;
  userId: number | null;
  username: string;
  fullName: string;
  roles: RoleInfo[];
  permissions: MenuPermission[];
}

interface PermissionContextType extends PermissionState {
  hasPermission: (menuCode: string, action?: string) => boolean;
  hasMenuAccess: (menuPath: string) => boolean;
  reload: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType>({
  loaded: false,
  userId: null,
  username: '',
  fullName: '',
  roles: [],
  permissions: [],
  hasPermission: () => false,
  hasMenuAccess: () => false,
  reload: async () => {},
});

export const usePermission = () => useContext(PermissionContext);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<PermissionState>({
    loaded: false,
    userId: null,
    username: '',
    fullName: '',
    roles: [],
    permissions: [],
  });

  const load = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setState(prev => ({ ...prev, loaded: true }));
      return;
    }
    try {
      const res = await authMeAPI.me();
      setState({
        loaded: true,
        userId: res.data.user_id,
        username: res.data.username,
        fullName: res.data.full_name || '',
        roles: res.data.roles || [],
        permissions: res.data.permissions || [],
      });
    } catch {
      setState(prev => ({ ...prev, loaded: true }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const hasPermission = useCallback((menuCode: string, action: string = 'READ') => {
    return state.permissions.some(
      p => p.menu_code === menuCode && p.actions.includes(action)
    );
  }, [state.permissions]);

  const hasMenuAccess = useCallback((menuPath: string) => {
    return state.permissions.some(p => p.menu_path === menuPath && p.actions.includes('READ'));
  }, [state.permissions]);

  return (
    <PermissionContext.Provider value={{ ...state, hasPermission, hasMenuAccess, reload: load }}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;
